/**
 * `gmDirectives` — the pure data + builders behind the Director ({@link GmDirectivePanel}). Kept out
 * of the component (which is just JSX over these) so the directive shapes, the copy and the GM gate
 * are unit-testable without a React render harness.
 *
 * The two GM→clients channels stay distinct, so the builders are too: the CUES fire EPHEMERAL
 * `Directive`s via `sendFx` (fire-and-forget, never persisted), the TRANSMISSION sends a DURABLE
 * `ViewStatePatch` via `setView` (persisted + replayed on reconnect; an explicit `null` clears it).
 * A new cue is a one-line `register(...)` in `effectRegistry.ts` + an entry here — NEVER a new wire
 * message type.
 */
import type { BannerTone, Directive } from "@lancer-vtt/shared";
import type { ViewStatePatch } from "./useGameSession";

/** One ephemeral cue the Director can fire. `type` MUST be a registered effect (`effectRegistry.ts`). */
export type Cue = { type: "glitch" | "impact" | "shake"; label: string; blurb: string; aria: string };

/** The cue keys, in panel order — each a registered ephemeral effect. */
export const CUES: Cue[] = [
  { type: "glitch", label: "GLITCH", blurb: "Signal tears. 0.7s", aria: "Fire glitch on every player screen" },
  { type: "impact", label: "IMPACT", blurb: "Hot white hit. 0.5s", aria: "Fire impact flash on every player screen" },
  { type: "shake", label: "SHAKE", blurb: "Heavy jolt. 0.45s", aria: "Fire screen shake on every player screen" },
];

/** Build the ephemeral FX directive for a cue. */
export function fxDirective(type: string): Directive {
  return { type };
}

/** The line under the cue keys once one fired: what went, and to how many connected players. */
export function cueSentLine(label: string, players: number): string {
  return `> ${label} sent to ${players} ${players === 1 ? "player" : "players"}`;
}

/** One transmission tone in the Director's picker. */
export type ToneOption = { tone: BannerTone; label: string; hint: string };

/** The banner tones, in picker order, with the one-line hint the picker shows for the chosen one. */
export const TONES: ToneOption[] = [
  { tone: "omninet", label: "OMNINET", hint: "A message over the net. Band across the top; the board stays playable." },
  { tone: "alert", label: "ALERT", hint: "Something is wrong, right now. Hazard band, three blinks, then holds." },
  { tone: "narration", label: "NARRATION", hint: "The GM is talking. Letterbox bars close in; the line sits in the lower bar." },
];

/** The preview's stand-in line while the message box is empty. */
export const SAMPLE_TRANSMISSION = "Cataphract down. Hold the ridge.";

/** Set a standing banner in a tone (durable): re-shown on a player's reconnect until cleared. */
export function bannerPatch(text: string, tone: BannerTone): ViewStatePatch {
  return { activeBanner: { text, tone } };
}

/** Clear the standing banner — an explicit `null` clears the field. */
export function clearBannerPatch(): ViewStatePatch {
  return { activeBanner: null };
}

/**
 * Whether the GM-directive surface is available to this session role — GM-only. This is the UI
 * affordance gate (it mirrors COMBAT.app's `isGm`); the server's `GameRoom.handle` is the real wall,
 * and `sendFx`/`setView` themselves no-op unless `role === "gm"`.
 */
export function canDirect(role: "gm" | "player" | null): boolean {
  return role === "gm";
}
